<template>
  <div>
    <NuxtLink target="_blank" class="hotel-card" :to="getHotelPageAddr(activity)" >
      <div class="hotel-card__label-container" v-if="false">
        <!-- Labels reserved for future use (promotions, deals) -->
      </div>
      <div class="hotel-card__content">
        <div class="hotel-card__image-container">
          <img
              v-if="activityImg"
              class="hotel-card__image"
              :alt="activity?.name"
              :src="activityImg"
              :srcset="`${activityImg} 1x, ${activityImg2x} 2x`"
              width="262" height="232"
              loading="lazy"
          />
          <PhotoPlaceholder class="hotel-img-placeholder" v-else/>
        </div>
        <div class="hotel-card__details">
          <div class="hotel-card__info">
            <div class="hotel-card__title-container">
              <div class="hotel-card__title">
                {{activity.normalized_name}}
              </div>
            </div>
            <div class="hotel-card__badges">
              <span v-if="categoryLabel" class="card-badge card-badge--category">{{ categoryLabel }}</span>
              <span v-if="activity.duration" class="card-badge card-badge--duration">{{ formatDuration(activity.duration) }}</span>
              <span v-if="hasFreeCancellation" class="card-badge card-badge--free-cancel">{{$t('Free cancellation')}}</span>
            </div>
            <div class="hotel-card__address" v-if="activity.location">
              <div class="hotel-card__address-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M9 16.4248L9.59737 17.0072C9.51898 17.0842 9.42582 17.1452 9.32324 17.1869C9.22065 17.2286 9.11066 17.25 8.99958 17.25C8.8885 17.25 8.77851 17.2286 8.67592 17.1869C8.57334 17.1452 8.48017 17.0842 8.40178 17.0072L8.39672 17.0014L8.38237 16.9874L8.33006 16.9354C8.03263 16.6359 7.74009 16.3317 7.45256 16.023C6.73065 15.2496 6.03939 14.4493 5.38031 13.6239C4.62769 12.6752 3.85819 11.6027 3.27347 10.5434C2.70141 9.50477 2.25 8.37949 2.25 7.3499C2.25 3.60528 5.28075 0.75 9 0.75C12.7192 0.75 15.75 3.60528 15.75 7.3499C15.75 8.37949 15.2986 9.50477 14.7265 10.5426C14.1418 11.6035 13.3732 12.6752 12.6197 13.6239C11.6978 14.7786 10.7131 15.884 9.66994 16.9354L9.61763 16.9874L9.60328 17.0014L9.59822 17.0064L9 16.4248ZM9 9.82487C9.67133 9.82487 10.3152 9.56411 10.7899 9.09997C11.2646 8.63582 11.5312 8.00631 11.5312 7.3499C11.5312 6.6935 11.2646 6.06399 10.7899 5.59984C10.3152 5.13569 9.67133 4.87494 9 4.87494C8.32867 4.87494 7.68484 5.13569 7.21014 5.59984C6.73543 6.06399 6.46875 6.6935 6.46875 7.3499C6.46875 8.00631 6.73543 8.63582 7.21014 9.09997C7.68484 9.56411 8.32867 9.82487 9 9.82487Z" fill="#292929"/>
                </svg>
              </div>
              <div class="hotel-card__address-text">
                <span v-if="activity.location.address_info">{{activity.location.address_info}}</span>
                <span v-else>{{activity.location.city_name}}</span>
              </div>
            </div>
          </div>
          <div class="hotel-card__description">
            {{ activity.description }}
          </div>
        </div>
      </div>
      <div class="hotel-card__price-container">
        <div class="hotel-card__price-indicator"></div>
        <div class="hotel-card__price-details">
          <div class="hotel-card__price-rating">
            <template v-if="activity.avg_rating && activity.avg_rating > 0">
              <div class="hotel-card__price-rating-text">
                <div class="hotel-card__price-rating-title">
                  <span v-if="activity.avg_rating >= 4.5">{{$t('Excellent')}}</span>
                  <span v-else-if="activity.avg_rating >= 3.5">{{$t('Very Good')}}</span>
                  <span v-else-if="activity.avg_rating >= 3">{{$t('Good')}}</span>
                  <span v-else>{{$t('Fair')}}</span>
                </div>
                <div class="hotel-card__price-rating-reviews" v-if="activity.overall_rating">
                  {{ Number(activity.overall_rating).toFixed(1) }} / 5
                </div>
              </div>
              <div class="hotel-card__price-rating-score">
                <div class="hotel-card__price-rating-score-text">{{ round(activity.avg_rating) }}</div>
              </div>
            </template>
          </div>
          <div class="hotel-card__price-info">
            <div class="hotel-card__price">
              <div class="hotel-card__price-value">
                {{ activity.price_from?.formatted }}
              </div>
              <div class="hotel-card__price-per">{{$t("per person")}}</div>
              <div class="hotel-card__price-includes">{{$t("includes taxes & fees")}}</div>
            </div>
          </div>
        </div>
      </div>
    </NuxtLink>
  </div>
</template>

<script setup lang="ts">
import {imageUrlWithProxy} from "~/utils/imageproxy";
import {useRoute} from "vue-router";
import dayjs from '~/utils/dayjs'

const route = useRoute();
const localePath = useLocalePath()
const props = defineProps({
  activity: {
    type: Object,
    required: true
  }
})

const activityImg = computed(() => {
  if (props.activity.cover_image_url) {
    return imageUrlWithProxy(props.activity.cover_image_url, 400, 300, 'fill')
  }
  return false
})

const activityImg2x = computed(() => {
  if (props.activity.cover_image_url) {
    return imageUrlWithProxy(props.activity.cover_image_url, 524, 464, 'fill')
  }
  return ''
})

const hasFreeCancellation = computed(() => {
  return props.activity.cancellation_policies?.some(
    (p: any) => p.refund_value == 100 && p.refund_type === 'percentage'
  )
})

const categoryLabel = computed(() => {
  // API returns categories array with {code, name}, use first one
  const cats = props.activity.categories
  if (cats?.length) return cats[0].name
  // Fallback: some list responses may include category_name directly
  return props.activity.category_name || ''
})

function formatDuration(seconds: number) {
  return dayjs.duration(seconds * 1000).humanize()
}

function getHotelPageAddr(activity: any): string {
  if (!activity) return ''
  if (!activity.id || !activity.slug) return ''
  const queryFromObject = Object.keys(route.query)?.length ? `?${Object.keys(route.query).map(key => `${key}=${route.query[key]}`).join('&')}` : ''
  const _url = `/experiences/${route.params.city}/${activity.id}-${activity.slug}${queryFromObject}`

  return localePath(_url)
}

function round(rating:number) {
  return ((rating ^ 0) === rating) ? (+rating).toFixed(1) : (+rating).toFixed(1)
}
</script>


<style scoped lang="scss">
$color-white: var(--white);
$color-light-gray: var(--grey-border);
$color-green: var(--light-green);
$color-dark-green: var(--green);
$color-dark-gray: var(--black);
$color-mid-gray: var(--dark-grey);
$color-yellow: var(--yellow);
$color-blue: var(--blue);

$tablet-border: 1024px;
$mobile-border: 767px;

.hotel-card {
  width: 100%;
  height: 232px;
  background: var(--white);
  border-radius: 10px;
  box-shadow: 0 0 0 2px var(--grey-border);
  justify-content: center;
  gap: 16px;
  display: inline-flex;
  position: relative;

  &:hover {
    .hotel-card__price-value {
      color: var(--blue);
      font-size: 24px;
      transition: font-size 0.3s;
    }
  }

  @media screen and (max-width: $tablet-border) and (min-width: $mobile-border) {
    height: fit-content;
  }

  @media screen and (max-width: $mobile-border) {
    height: 100%;
    flex-direction: column;
    gap: 0;
  }

  &__label-container {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    display: inline-flex;
    position: absolute;
    left: 0;
    top: 16px;
    border-radius: 4px 4px 4px 0px;
    background: var(--Label-Green, #44B50C);
  }

  &__label {
    padding: 6px 10px;
    background: $color-green;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    display: flex;

    &-text {
      color: $color-white;
      font-size: 12px;

      font-weight: 600;
      line-height: 16px;
      word-wrap: break-word;
    }
  }

  &__content {
    flex: 1 1 0;
    align-self: stretch;
    justify-content: flex-start;
    align-items: flex-start;
    display: flex;

    @media screen and (max-width: $mobile-border) {
      height: 100%;
      border-bottom: 2px solid $color-light-gray;
    }
  }

  &__image-container {
    width: 262px;
    height: 100%;
    border-radius: 10px;
    overflow: hidden;
    justify-content: center;
    align-items: center;
    display: flex;

    .hotel-img-placeholder {
      :deep(.icon) {
        font-size: 40px !important;
      }
    }

    @media screen and (max-width: $tablet-border) and (min-width: $mobile-border) {
      width: 194px;
      height: 244px;
    }

    @media screen and (max-width: $mobile-border) {
      width: 100%;
      max-width: 131px;
      max-height: 262px;
      min-height: 252px;
      border-radius: 10px 0 0 0;
    }
  }

  &__image {
    object-fit: cover;
    width: 100%;
    height: 100%;

    @media screen and (max-width: 768px) {
      width: auto;
      height: 252px;
    }
  }

  &__details {
    flex: 1 1 0;
    padding: 20px 0 20px 24px;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 8px;
    display: inline-flex;
    max-width: 384px;

    @media screen and (max-width: $tablet-border) and (min-width: $mobile-border) {
      gap: 8px;
    }

    @media screen and (max-width: $mobile-border) {
      padding: 14px 16px 16px 16px;
    }
  }

  &__info {
    align-self: stretch;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 6px;
    display: flex;
  }

  &__title-container {
    align-self: stretch;
    align-items: center;
    gap: 8px;
    display: flex;

    @media screen and (max-width: $mobile-border) {
      display: inline-flex;
    }
  }

  &__title {
    color: $color-dark-gray;
    max-width: 255px;
    font-size: 20px;
    font-weight: 600;
    line-height: 24px;
    display: inline-flex;

    @media screen and (max-width: $tablet-border) and (min-width: $mobile-border) {
      max-width: 169px;
    }

    @media screen and (max-width: $mobile-border) {
      max-width: 100%;
      font-size: 16px;
    }
  }

  &__badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  &__address {
    align-self: stretch;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 8px;
    display: inline-flex;

    &-text {
      flex: 1 1 0;
      color: $color-mid-gray;
      font-size: 14px;

      font-weight: 400;
      line-height: 20px;
      word-wrap: break-word;
    }
  }

  &__description {
    color: $color-mid-gray;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    word-wrap: break-word;
    height: 64px;
    overflow: hidden;
    text-overflow: ellipsis;
    max-height: 72px;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    box-sizing: content-box;

    @media screen and (max-width: $tablet-border) {
      display: none;
    }
  }

  &__price-container {
    margin: 20px 20px 20px 0;
    border-left: 1px solid var(--grey-border);
    gap: 12px;
    display: flex;
    flex: 1;
    max-width: 168px;
    justify-content: flex-end;

    @media screen and (max-width: $tablet-border) and (min-width: $mobile-border) {
      width: 100%;
      max-width: 189px;
      justify-content: flex-end;
      margin: 27px 17px 10px 0;
    }

    @media screen and (max-width: $mobile-border) {
      max-width: 100%;
      padding: 18px 16px;
      margin: 0;
      gap: 0;
    }

    &-indicator {
      width: 192px;
      align-self: stretch;
      transform: rotate(-90deg);
      transform-origin: 0 0;
      background: $color-light-gray;
    }
  }

  &__price {
    &-details {
      max-width: 155px;
      flex-direction: column;
      justify-content: space-between;
      align-items: flex-end;
      display: flex;

      @media screen and (max-width: $mobile-border) {
        max-width: 100%;
        flex-direction: row;
        justify-content: space-between;
        width: 100%;
      }
    }

    &-info {
      flex-direction: column;
      justify-content: flex-end;
      align-items: flex-end;
      gap: 4px;
      display: flex;
      text-align: right;
    }

    &-value {
      color: var(--black);
      text-align: right;
      font-size: 20px;
      font-weight: 600;
      line-height: 120%;
      margin-bottom: 2px;
    }

    &-per {
      font-size: 12px;
      color: $color-mid-gray;
      text-align: right;
    }

    &-includes {
      align-self: stretch;
      text-align: right;
      color: $color-mid-gray;
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      word-wrap: break-word;
    }

    &-rating {
      justify-content: flex-end;
      align-items: center;
      gap: 8px;
      display: inline-flex;

      @media screen and (max-width: $mobile-border) {
        flex-direction: row-reverse;
      }

      &-text {
        flex-direction: column;
        justify-content: center;
        align-items: flex-end;
        display: inline-flex;

        @media screen and (max-width: $mobile-border) {
          align-items: flex-start;
        }
      }

      &-title {
        color: $color-dark-gray;
        font-size: 14px;
  
        font-weight: 500;
        line-height: 20px;
        word-wrap: break-word;
      }

      &-reviews {
        color: $color-mid-gray;
        font-size: 12px;
  
        font-weight: 400;
        line-height: 16px;
        word-wrap: break-word;
      }

      &-score {
        height: 40px;
        width: 40px;
        padding: 8px 0;
        background: $color-dark-green;
        border-radius: 4px;
        justify-content: center;
        align-items: center;
        gap: 4px;
        display: flex;

        &-text {
          flex: 1 1 0;
          text-align: center;
          color: $color-white;
          font-size: 16px;
    
          font-weight: 600;
          line-height: 24px;
          word-wrap: break-word;
        }
      }

    }

  }
}

.card-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;

  &--category {
    background: var(--blue-highlight, #e8f0fe);
    color: var(--blue);
  }

  &--duration {
    background: var(--light-grey);
    color: var(--dark-grey);
  }

  &--free-cancel {
    background: #e8f5e9;
    color: #2e7d32;
  }
}
</style>
